import { useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@mesh.ai/rabbit';
import { useDispatch } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';

import { ArrowBack } from 'assets/assetsV1/iconComponents';

import { BottomDrawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { ApproverWiseAllocationTable } from './ApproverWiseAllocationTable';
import { BudgetTimeline } from './BudgetTimeline';
import { CurrentPayDetailsTreeTable } from './CurrentPayDetailsTreeTable';
import { TotalRaiseBudgetCard } from './TotalRaiseBudgetCard';
import { MOCK_APPROVER_WISE_ROW_DATA, MOCK_HIERARCHICAL_ROW_DATA } from './shared/stakeholderConstants';
import {
  setBudgetTimelineStatus,
  setIsBudgetDrawerOpen,
  setModifiedCurrentPayDetailsRows,
  setOriginalCurrentPayDetailsRows,
  setModifiedApproverWiseAllocationRows,
  setOriginalApproverWiseAllocationRows,
  useCompensationDrawersState,
} from './slice/compensationDrawersSlice';

const tableOptions = [
  { value: 'functionTree', label: 'Function tree' },
  { value: 'position', label: 'Position' },
];

const MOCK_EMPLOYEES = [
  {
    id: 16033,
    companyId: 1,
    displayName: 'Sherry Decker',

    designation: 'Information systems manager',
    ownerType: null,
    employeeFunction: null,
    email: 'sherry.decker@abc.co',
    personalEmail: 'sherry.decker@abc.co',
    workEmail: 'sherry.decker@abc.co',
    preferredName: 'Sherry',
  },
  {
    id: 16190,
    companyId: 2,
    displayName: 'Vernon Huff',

    designation: 'Teaching laboratory technician',
    ownerType: null,
    employeeFunction: null,
    email: 'vernon.huff@abc.co',
    personalEmail: 'vernon.huff@abc.co',
    workEmail: 'vernon.huff@abc.co',
    preferredName: 'Vernon',
  },
  {
    id: 16366,
    companyId: 1,
    displayName: 'Jose Allen',

    designation: 'Cytogeneticist',
    ownerType: null,
    employeeFunction: null,
    email: 'jose.allen@abc.co',
    personalEmail: 'jose.allen@abc.co',
    workEmail: 'jose.allen@abc.co',
    preferredName: 'Jose',
  },
];

export const BudgetAllocationDrawer = () => {
  const { isBudgetDrawerOpen, budgetTimelineStatus } = useCompensationDrawersState();
  const dispatch = useDispatch();
  const cohortDetails = useSelectedView();

  const [tableType, setTableType] = useState(tableOptions[0]);
  const [isDataInitialized, setIsDataInitialized] = useState(false);

  // Initialize data when drawer opens
  useEffect(() => {
    if (isBudgetDrawerOpen && !isDataInitialized) {
      // Initialize CurrentPayDetailsTreeTable data
      const processedCurrentPayRows = MOCK_HIERARCHICAL_ROW_DATA.map((row) => ({
        ...row,
        increasedBudget: Number(row.totalNewBudget) - Number(row.currentPayroll),
      }));

      // Update Redux state for CurrentPayDetailsTreeTable
      dispatch(setModifiedCurrentPayDetailsRows(processedCurrentPayRows));
      dispatch(setOriginalCurrentPayDetailsRows(processedCurrentPayRows));

      // Initialize ApproverWiseAllocationTable data when API data is available
      const processedApproverRows = MOCK_APPROVER_WISE_ROW_DATA.rows.map((row) => ({
        ...row,
        approver: row.employee,
        employees: MOCK_EMPLOYEES,
        id: row.id,
        currentPayroll: 100000,
        increasedAllocation: 10000,
        totalAllocation: 110000,
        adjustedBudget: 0,
      }));

      // Update Redux state for ApproverWiseAllocationTable
      dispatch(setModifiedApproverWiseAllocationRows(processedApproverRows));
      dispatch(setOriginalApproverWiseAllocationRows(processedApproverRows));

      setIsDataInitialized(true);
    }
  }, [isBudgetDrawerOpen, isDataInitialized, dispatch]);

  // Reset initialization flag when drawer closes
  useEffect(() => {
    if (!isBudgetDrawerOpen) {
      setIsDataInitialized(false);
    }
  }, [isBudgetDrawerOpen]);

  const onClose = () => {
    dispatch(setIsBudgetDrawerOpen(false));
  };

  const onSave = () => {
    // Add save functionality here
    onClose();
  };

  const onNext = () => {
    dispatch(setBudgetTimelineStatus('BASELINE_SET'));
  };

  const onBack = () => {
    dispatch(setBudgetTimelineStatus(null));
  };

  return (
    <BottomDrawer isOpen={isBudgetDrawerOpen} onClose={onClose} placement="bottom">
      <DrawerContent width="auto" ml="40px !important" maxH="calc(100vh - 40px)">
        <DrawerHeader onClose={onClose}>
          <Box padding="10px 10px" display="flex" justifyContent="space-between" alignItems="center">
            {budgetTimelineStatus === null ? (
              <Box display="flex" alignItems="center" gap="8px">
                <TextBodyMedium bold whiteSpace="nowrap">
                  Budget allocation for
                </TextBodyMedium>
                <Box>
                  <Select
                    value={tableType.value}
                    onValueChange={(value) => {
                      const selectedOption = tableOptions.find((option) => option.value === value);
                      if (selectedOption) {
                        setTableType(selectedOption);
                      }
                    }}
                  >
                    <SelectTrigger style={{ minWidth: '140px' }}>
                      <SelectValue placeholder="Select workflow" />
                    </SelectTrigger>
                    <SelectContent>
                      {tableOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Box>
              </Box>
            ) : (
              <ListItemComp
                title="Approver wise allocation"
                withoutBackground
                LeftComponent={ArrowBack}
                onClick={onBack}
              />
            )}
            <Box width="370px">
              <BudgetTimeline status={budgetTimelineStatus} />
            </Box>
          </Box>
        </DrawerHeader>
        <DrawerBody>
          <Box padding="4px 4px 8px 4px" background="white">
            <Box
              display="flex"
              padding="4px"
              alignItems="flex-start"
              alignSelf="stretch"
              borderRadius="8px"
              background="#EBEFF3"
              flexDirection="column"
            >
              <TotalRaiseBudgetCard />
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap="12px"
                alignSelf="stretch"
                borderRadius="8px"
                border="1px solid #F5F7F9"
                bg="#FDFDFD"
              >
                {budgetTimelineStatus === 'BASELINE_SET' ? (
                  <ApproverWiseAllocationTable gridStatePersistenceKey="approver-wise-allocation-table" columns={[]} />
                ) : (
                  <CurrentPayDetailsTreeTable gridStatePersistenceKey="current-pay-details-tree-table" columns={[]} />
                )}
              </Box>
            </Box>
          </Box>
        </DrawerBody>
        <DrawerFooter display="flex" justifyContent="space-between">
          {budgetTimelineStatus === null && (
            <>
              <Button variant="outlined" mr={4} onClick={onClose}>
                Cancel
              </Button>
              <Button variant="filled" colorScheme="blue" onClick={onNext}>
                Next
              </Button>
            </>
          )}
          {budgetTimelineStatus === 'BASELINE_SET' && (
            <>
              <Button variant="tertiary" onClick={onBack}>
                Back
              </Button>
              <Box>
                <Button variant="outlined" mr={4} onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="filled" colorScheme="blue" onClick={onSave}>
                  Save
                </Button>
              </Box>
            </>
          )}
        </DrawerFooter>
      </DrawerContent>
    </BottomDrawer>
  );
};
